import React, { useState, useCallback, memo } from 'react'
import { Modal, View, TextInput, ActionIcon, SearchInput, EmptyPlaceholder, ActivityIndicator, Checkbox, Text, AvatarInfo } from '@/components'
import { PropsOf } from '@codeleap/types'
import { createStyles } from '@codeleap/styles'
import { Chat, navigateTo, routes, textVariant } from '@/config'
import { ChatChannelInstance, useChatFilters } from '@packages/chat'
import { ChannelSort } from 'stream-chat'
import { onUpdate } from '@codeleap/hooks'
import { useChatPreviewAvatar, useChatProps } from '@/hooks'

const chatSort: ChannelSort = [{ last_message_at: -1 }, { updated_at: -1 }]

export type ForwardMessageModalProps = PropsOf<typeof Modal>

export const ForwardMessageModal = (props: ForwardMessageModalProps) => {
  const { ...modalProps } = props
  const [messageText, setMessageText] = useState('')

  const user = Chat.Store.useUser()
  const { filters, searchText, setSearchText } = useChatFilters(user)
  const { isLimitReached } = Chat.Store.useIsForwardLimitReached()
  const selectedChatCount = Chat.Store.state.use(s => s.selectedChats?.length || 0)

  const { flatListProps } = Chat.Services.useChats(
    { filters, sort: chatSort },
    {
      staleTime: 1000 * 60 * 5,
    },
  )

  const data = flatListProps?.data || []
  const loading = flatListProps?.loading

  const wasOpenedRef = React.useRef(false)

  onUpdate(() => {
    if (props.visible) {
      wasOpenedRef.current = true
    }

    if (!props.visible && wasOpenedRef.current) {
      setSearchText('')
      setMessageText('')
      Chat.Store.setSelectedChats([])
      Chat.Store.setSelectMode(null)
      wasOpenedRef.current = false
    }
  }, [props.visible, setSearchText])

  const handleForward = useCallback(async () => {
    const channelsToForward = Chat.Store.state.get().selectedChats || []
    const selectedMessages = Chat.Store.state.get().selectedMessages || []

    if (channelsToForward.length === 0) return

    Chat.Store.state.set({
      selectMode: null,
    })

    props?.toggle?.()

    await Chat.Services.forwardMessages({
      channelsToForward,
      messages: selectedMessages,
      messageText,
      onFinally: () => {
        Chat.Store.state.set({
          selectedMessages: [],
          selectedChats: [],
        })
        Chat.Store.setToastMessage('Message forwarded')
      },
      onOpenChannel: (channel) => {
        navigateTo(`/${routes.Chat.Chat}`, { c: channel.id })
      },
    })
  }, [messageText, props])

  return (
    <Modal
      title='Send to'
      style={['center', 'box:noPadding', { titleWrapper: styles.titleWrapper, box: styles.box }]}
      showClose={true}
      {...modalProps}
    >
      <View style={styles.wrapper}>
        <View style={styles.searchWrapper}>
          <SearchInput
            debugName='ForwardMessage:Search'
            leftIcon={{ icon: 'search' }}
            placeholder='Search'
            onSearchChange={setSearchText}
            debounce={300}
            onTypingChange={() => { }}
            style={['fullWidth', 'noError']}
          />

          {selectedChatCount > 0 && (
            <Text
              text={`${selectedChatCount} selected`}
              style={[
                styles.selectedCount,
                selectedChatCount === 0 && styles.selectedCountHidden,
                'mb:1',
              ]}
            />
          )}
        </View>

        <View style={styles.listWrapper}>
          <Text
            text='Recent chats'
            style={['c1', 'semiBold', 'paddingHorizontal:2', 'marginVertical:0.5']}
          />

          {loading && (
            <View style={styles.loadingWrapper}>
              <ActivityIndicator />
            </View>
          )}

          {!loading && data.length === 0 && (
            <EmptyPlaceholder
              icon='users-01'
              title='No chats found'
              description={searchText ? 'No chats match your search.' : 'There are no chats here.'}
              style={['compact']}
            />
          )}

          {!loading && data.length > 0 && (
            <View style={styles.chatList}>
              {data.map((channel) => (
                <ChatItem key={channel.id} channel={channel} isLimitReached={isLimitReached} />
              ))}
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <View style={styles.inputContainer}>
            <View style={styles.inputWrapper}>
              <TextInput
                value={messageText}
                onValueChange={setMessageText}
                placeholder='Type your message...'
                style={['fullWidth', 'noError', { input: styles.input }]}
                debugName='ForwardMessage:Input'
              />
            </View>
            <ActionIcon
              icon='arrow-up'
              onPress={handleForward}
              disabled={selectedChatCount === 0}
              style={[
                'iconSize:2',
                styles.sendButton,
                selectedChatCount === 0 && styles.sendButtonDisabled,
              ]}
              debugName='ForwardMessage:Forward'
            />
          </View>
        </View>
      </View>
    </Modal>
  )
}

type ChatItemProps = {
  channel: ChatChannelInstance
  isLimitReached: boolean
}

const ChatItemComponent = ({ channel, isLimitReached }: ChatItemProps) => {
  const channelId = channel.id
  const isSelected = Chat.Store.state.use(
    s => !!s.selectedChats?.find(c => c.id === channelId),
  )

  const previewAvatar = useChatPreviewAvatar({ channel })
  const { displayTitle } = useChatProps({ channel })

  const handleSelect = useCallback(() => {
    Chat.Store.selectChat(channel)
  }, [channel])

  const isDisabled = !isSelected && isLimitReached

  const handleRowClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement

    if (target.closest('[data-checkbox-wrapper]') || target.closest('motion.div')) {
      return
    }
    handleSelect()
  }, [handleSelect])

  return (
    <View
      style={[styles.chatItem, { pointerEvents: isDisabled ? 'none' : 'auto' }]}
      onClick={handleRowClick}
    >
      <AvatarInfo
        title={displayTitle}
        avatar={previewAvatar}
        style={['flex', 'fullWidth', { avatarImage: styles.avatar, avatarWrapper: styles.avatar }]}
      />
      <View
        data-checkbox-wrapper
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
        }}
        style={{ pointerEvents: 'auto' }}
      >
        <Checkbox
          style={['circle']}
          value={isSelected}
          onValueChange={handleSelect}
          disabled={isDisabled}
        />
      </View>
    </View>
  )
}

const ChatItem = memo(ChatItemComponent, (prevProps, nextProps) => {
  return prevProps.channel.id === nextProps.channel.id && prevProps?.isLimitReached === nextProps.isLimitReached
})

const styles = createStyles(theme => ({
  titleWrapper: {
    ...theme.spacing.padding(2),
    ...theme.spacing.paddingTop(4),
    ...theme.spacing.paddingBottom(0),
  },
  box: {
    maxWidth: 480,
    width: '100%',
    maxHeight: 654,
    borderRadius: theme.radius.medium,
  },
  wrapper: {
    ...theme.presets.column,
    width: '100%',
    height: '100%',
    maxHeight: '70vh',
  },
  searchWrapper: {
    ...theme.spacing.paddingHorizontal(2),
    ...theme.spacing.paddingTop(1),
    ...theme.presets.center,
    ...theme.presets.alignStart,
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  listWrapper: {
    ...theme.presets.column,
    ...theme.spacing.paddingBottom(2),
    ...theme.spacing.paddingTop(0.5),

    flex: 1,
    overflowY: 'auto',
    minHeight: 415,
    maxHeight: 415,
    '&::-webkit-scrollbar': {
      width: 8,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.baseColors.neutralSolid300,
      borderRadius: theme.radius.small,
      border: 'none',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.baseColors.neutralSolid400,
    },
  },
  loadingWrapper: {
    ...theme.presets.center,
    minHeight: 350,
  },
  chatList: {
    ...theme.presets.column,
    minHeight: 350,
  },
  chatItem: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    gap: theme.spacing.value(2),
    ...theme.spacing.paddingHorizontal(2),
    height: 56,
    minHeight: 56,
    maxHeight: 56,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    borderBottom: `1px solid ${theme.colors.stroke}`,
    '&:hover': {
      backgroundColor: theme.colors.rowBgHover,
    },
  },
  footer: {
    ...theme.presets.column,
    ...theme.spacing.padding(2),
    backgroundColor: theme.colors.bgDefault,
  },
  inputContainer: {
    ...theme.presets.row,
    ...theme.presets.center,
    width: '100%',
    gap: theme.spacing.value(1),
  },
  inputWrapper: {
    flex: 1,
  },
  input: {
    minHeight: 40,
    maxHeight: 52,
    resize: 'none',
    overflow: 'auto',
  },
  sendButton: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.spacing.padding(0),
  },
  sendButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  description: {
    ...textVariant('p3'),
    fontWeight: 500,
    color: theme.baseColors.highlightTextHeading,
  },
  selectedCount: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.baseColors.neutralSolid500,
    minHeight: 16,
  },
  selectedCountHidden: {
    opacity: 0,
    visibility: 'hidden',
  },
  avatar: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxWidth: theme.size.largeish,
    maxHeight: theme.size.largeish,
  },
}))
